import { t } from '../../i18n';
import type { ApiImage } from '../lib/media';
import { BackButton } from './BackButton';
import { Thumb } from './Thumb';
import styles from './ui.module.css';

/**
 * Шапка карточки персонажа: кадр во всю ширину и имя поверх него.
 *
 * В макете это единственное место, где заголовок лежит НА изображении, — и
 * поэтому же здесь единственное затемнение в приложении. Смысл в том, что
 * досье открывают ради лица: имя под кадром отодвигало бы его вниз, а сам
 * кадр без имени не говорит, чьё оно.
 *
 * Без фото шапка остаётся текстовой: пустой прямоугольник в полэкрана
 * сообщает только то, что фотографию не приложили.
 */
export function Hero({
  image,
  caption,
  title,
  ratio = 'hero',
  onBack,
}: {
  image: ApiImage | null;
  /** Строка над именем: кем человек числится в Мезе и номер досье. */
  caption: string;
  title: string;
  /**
   * `hero` обрезает кадр до постоянной пропорции — так у досье, где под
   * шапкой сразу идут факты. `natural` оставляет кадр целиком: на карточке
   * внешки смотреть приходят именно его, и срезанная макушка там не годится.
   */
  ratio?: 'hero' | 'natural';
  /**
   * Кнопка «назад» ПОВЕРХ кадра. В холстах у экранов с кадром шапки панели
   * нет вовсе: фотография начинается от верхнего края, а возврат лежит на ней
   * обведённым квадратом. Шапка над кадром съедала полсотни пикселей и
   * повторяла словом то, что и так видно.
   */
  onBack?: () => void;
}) {
  // Кадра без рамки не бывает: фотографии нет — в кадре заглушка. Прежняя
  // текстовая шапка давала карточке второй облик, и досье с фото и без
  // выглядели как два разных экрана.
  return (
    <div className={styles.heroFrame}>
      {onBack === undefined ? null : <BackButton overlay onClick={onBack} />}
      <Thumb
        image={image}
        fill
        ratio={ratio}
        scrim
        alt={t('ui.hero.appearanceAlt', { title })}
        gallery={[image]}
        overlay={
          <>
            <span className={styles.heroCaption}>{caption}</span>
            <span className={styles.heroTitle}>{title}</span>
          </>
        }
      />
    </div>
  );
}
